import React, { ReactNode, useEffect, useState } from 'react';
import { TextInput } from './text-input';
import {
  OnValueChange,
  PatternFormat,
  NumericFormat,
} from 'react-number-format';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

function formatDateString(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);

  const date = new Date(Date.UTC(year, month - 1, day));

  const formattedMonth = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // getUTCMonth() returns 0-11
  const formattedDay = date.getUTCDate().toString().padStart(2, '0');
  const formattedYear = date.getUTCFullYear();

  return `${formattedMonth}${formattedDay}${formattedYear}`;
}

const ISOStringFormat = (d: string) => {
  const month = d.slice(0, 2);
  const day = d.slice(2, 4);
  const year = d.slice(4, 8);

  if (day && month && year && year.length === 4) {
    const m = parseInt(month, 10) - 1;
    const d = parseInt(day, 10);
    const y = parseInt(year, 10);

    try {
      const ISOdate = new Date(y, m, d).toISOString();
      return ISOdate;
    } catch {
      console.error('Invalid date');
      return null;
    }
  }
  return null;
};

function convertToISODateFormat(dateString: string): string {
  const month = dateString.slice(0, 2);
  const day = dateString.slice(2, 4);
  const year = dateString.slice(4, 8);

  // Format to yyyy-mm-dd
  return `${year}-${month}-${day}`;
}

export const DateInput: React.FC<{
  type?: React.HTMLInputTypeAttribute;
  name?: string;
  placeholder?: string;
  leftIcon?: ReactNode;
  value: string;
  format: string;
  disabled?: boolean;
  onValueChange?: OnValueChange;
  onChange?: ({
    value,
    formattedValue,
    convertedValue,
  }: {
    value: string | null;
    formattedValue: string;
    convertedValue: string;
  }) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  valueIsNumericString?: boolean;
  className?: string;
  showError?: boolean;
  showBorder?: boolean;
}> = ({
  disabled,
  type,
  format,
  name,
  value,
  placeholder,
  leftIcon,
  onChange,
  onBlur,
  onValueChange,
  autoFocus,
  valueIsNumericString,
  className,
  showError = true,
  showBorder = true,
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;
  const initialValue = value ? formatDateString(value) : '';

  const [date, setDate] = useState<string | null>(initialValue);

  const fullDateDisplayFormat = '##/##/####';

  useEffect(() => {
    if (value) {
      const dateStr = formatDateString(value);
      setDate(dateStr);
    }
  }, [value]);

  const handleChange = (values: { formattedValue: string; value: string }) => {
    const { formattedValue, value } = values;

    setDate(value);

    if (date) {
      onChange &&
        onChange({
          value: ISOStringFormat(value),
          formattedValue: formattedValue,
          convertedValue: convertToISODateFormat(value),
        });
    }
  };

  return (
    <>
      <PatternFormat
        format={fullDateDisplayFormat}
        value={date}
        onValueChange={(values: { formattedValue: string; value: string }) =>
          handleChange(values)
        }
        disabled={disabled}
        customInput={TextInput}
        valueIsNumericString={valueIsNumericString}
        className={className}
        leftIcon={leftIcon}
        showBorder={showBorder}
      />
      {name && error && showError ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </>
  );
};
